import React from "react";
import Text from "@src/lib/ui/Text";
import Link from "next/link";
import { useRouter } from "next/router";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CERTIFICATE_IMPORTS_PATH = "/settings/certificate-imports" as any;

import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@src/lib/ui/Sidebar";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import useUser from "@src/features/app/hooks/useUser";
import { UserType } from "@src/types.generated";
import { useBillingInitialized } from "@src/features/settings/hooks/billing/useBillingInitialized";
import ArrowLeftIcon from "@src/lib/icons/18px/arrow-left";
import OpieOutlineIcon from "@src/lib/icons/18px/opie-outline";
import BucketPaintOutlineIcon from "@src/lib/icons/18px/bucket-paint-outline";
import ShieldCheckOutlineIcon from "@src/lib/icons/18px/shield-check-outline";
import LanguageIcon from "@src/lib/icons/18px/language";
import CoinsOutlineIcon from "@src/lib/icons/18px/coins-outline";
import FileArrowInIcon from "@src/lib/icons/18px/file-arrow-in";
import AwardCertificateOutlineIcon from "@src/lib/icons/18px/award-certificate-outline";
import CalendarOutlineIcon from "@src/lib/icons/18px/calendar-outline";
import TagOutlineIcon from "@src/lib/icons/18px/tag-outline";

type SettingsNavItem = {
  label: string;
  href?: string;
  isActive: (pathname: string) => boolean;
  Icon: React.ComponentType<{ className?: string }>;
  disabled?: boolean;
};

const AdminLeftNavSettingsNav: React.FC = () => {
  const { pathname } = useRouter();
  const { userActionPermissions } = useActionPermissions();
  const { user } = useUser();
  const billingInitialized = useBillingInitialized({
    skip: !userActionPermissions?.manageBilling,
  });

  const settingsItems: SettingsNavItem[] = [
    ...(userActionPermissions?.changeOrgSettings
      ? [
          {
            label: "Ask Opus",
            href: "/settings/ask-opus",
            isActive: (currentPath: string) =>
              currentPath === "/settings" ||
              currentPath === "/settings/ask-opus",
            Icon: OpieOutlineIcon,
          },
          {
            label: "Brand",
            href: "/settings/brand",
            isActive: (currentPath: string) =>
              currentPath === "/settings/brand",
            Icon: BucketPaintOutlineIcon,
          },
          {
            label: "Wage & Hour",
            href: "/settings/compliance",
            isActive: (currentPath: string) =>
              currentPath === "/settings/compliance",
            Icon: ShieldCheckOutlineIcon,
          },
          {
            label: "Languages",
            href: "/settings/language",
            isActive: (currentPath: string) =>
              currentPath === "/settings/language",
            Icon: LanguageIcon,
          },
        ]
      : []),
    {
      label: "Tags",
      href: "/settings/tags",
      isActive: (currentPath: string) => currentPath === "/settings/tags",
      Icon: TagOutlineIcon,
    },
    ...(billingInitialized && userActionPermissions?.manageBilling
      ? [
          {
            label: "Billing",
            href: "/settings/billing",
            isActive: (currentPath: string) =>
              currentPath === "/settings/billing",
            Icon: CoinsOutlineIcon,
          },
        ]
      : []),
    ...(userActionPermissions?.manageOrgIntegrations
      ? [
          {
            label: "Training Imports",
            href: "/settings/training-imports",
            isActive: (currentPath: string) =>
              currentPath === "/settings/training-imports",
            Icon: FileArrowInIcon,
          },
          {
            label: "Certificate Imports",
            href: CERTIFICATE_IMPORTS_PATH,
            isActive: (currentPath: string) =>
              currentPath === CERTIFICATE_IMPORTS_PATH,
            Icon: AwardCertificateOutlineIcon,
          },
        ]
      : []),
    // Scheduled invites is admin-only at the backend even though `inviteOthers`
    // is on by default for managers — gate on both or the link 404s.
    ...(userActionPermissions?.inviteOthers && user?.userType === UserType.Admin
      ? [
          {
            label: "Scheduled Invites",
            href: "/settings/scheduled-invites",
            isActive: (currentPath: string) =>
              currentPath === "/settings/scheduled-invites",
            Icon: CalendarOutlineIcon,
          },
        ]
      : []),
  ];

  return (
    <>
      <SidebarHeader className="px-3 pt-4 pb-0 group-data-[collapsible=icon]:px-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Back">
              <Link href="/">
                <ArrowLeftIcon />
                Back
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {settingsItems.map(({ label, href, isActive, Icon, disabled }) =>
                disabled || !href ? (
                  <SidebarMenuItem key={label}>
                    <SidebarMenuButton
                      disabled
                      tooltip={label}
                      className="cursor-not-allowed hover:bg-transparent"
                    >
                      <Icon />
                      {label}
                      <Text
                        variant="micro-title"
                        className="ml-auto rounded-full bg-gray-2 px-1.5 py-0.5 leading-none text-gray-8"
                      >
                        Soon
                      </Text>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ) : (
                  <SidebarMenuItem key={href}>
                    <SidebarMenuButton
                      asChild
                      tooltip={label}
                      isActive={isActive(pathname)}
                    >
                      <Link
                        href={href as React.ComponentProps<typeof Link>["href"]}
                      >
                        <Icon />
                        {label}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ),
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </>
  );
};

export default AdminLeftNavSettingsNav;
